import React, { useEffect, useMemo } from 'react';
import {
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core';
import { useFormikContext, Form } from 'formik';
import clsx from 'clsx';
import i18n from 'i18n-js';
import ClearIcon from '@material-ui/icons/Clear';
import images from 'assets/images';
import { Input, FormikMultiSelect } from 'components/shared';
import { useStyles } from '../styles';
import { IdName } from '../../restaurantsPage/components/NewRestaurantDialog';
import { useGroupForm } from '../hooks/useGroupForm';
import { Autocomplete } from '@material-ui/lab';

export enum MenuOption {
    Copy = 'copy',
    Empty = 'empty',
}

export type CreateGroupForm = {
    name: string;
    selectedRestaurants: IdName[];
    selectedUsers: IdName[];
    menuOption: MenuOption;
    selectedMenus: IdName[];
    catalogName?: string;
};

export default function GroupFormFields({
    restaurants,
    isEditMode = false,
}: {
    restaurants: IdName[];
    isEditMode?: boolean;
}) {
    const classes = useStyles();
    const { values, setFieldValue, errors, touched } = useFormikContext<CreateGroupForm>();
    const { users, handleRestaurantsOnChange, handleRestaurantRemove, handleUserRemove, menus } =
        useGroupForm();

    const selectionKey = useMemo(
        () =>
            values.selectedRestaurants
                .map((r) => r.id)
                .sort()
                .join(','),
        [values.selectedRestaurants]
    );

    useEffect(() => {
        handleRestaurantsOnChange(values.selectedRestaurants);
    }, [selectionKey, handleRestaurantsOnChange, values.selectedRestaurants]);

    function removeRestaurant(option: IdName) {
        return handleRestaurantRemove(
            option,
            values.selectedRestaurants,
            values.selectedUsers,
            values.selectedMenus,
            setFieldValue
        );
    }

    const Chips = ({
        items,
        onDelete,
    }: {
        items: IdName[];
        onDelete: (item: IdName) => void | Promise<void>;
    }) => (
        <div className={clsx(classes.mTop20, classes.mBot20)}>
            <Grid container spacing={1}>
                {items.map((item) => (
                    <Grid item key={item.id} className={classes.chipContainer}>
                        <Chip
                            label={item.name}
                            className={classes.chip}
                            color="primary"
                            onDelete={() => onDelete(item)}
                            deleteIcon={<ClearIcon />}
                        />
                    </Grid>
                ))}
            </Grid>
        </div>
    );

    return (
        <Form className={classes.formInnerContainer}>
            <Grid container spacing={8}>
                <Grid item xs={12} md={6} lg={5}>
                    <Typography className={classes.sectionTitle}>
                        {i18n.t('restaurant.enterGroupName')}
                    </Typography>
                    <Input
                        fullWidth
                        id="name"
                        name="name"
                        type="text"
                        error={touched.name && errors.name}
                        variant="filled"
                        placeholder={i18n.t('restaurant.enterGroupName')}
                        label={i18n.t('common.groupName')}
                    />

                    <Typography className={classes.sectionTitle}>
                        {i18n.t('restaurant.assignRestaurantToGroup')}
                    </Typography>
                    <FormikMultiSelect
                        name="selectedRestaurants"
                        options={restaurants}
                        getOptionLabel={(option) => option.name}
                        error={touched.selectedRestaurants && errors.selectedRestaurants}
                        inputLabel={i18n.t('common.selectFromTheList')}
                        popupIcon={images.icons.triangleDown}
                        getOptionSelected={(option, value) => option.id === value.id}
                    />

                    {values.selectedRestaurants.length > 0 && (
                        <Chips items={values.selectedRestaurants} onDelete={removeRestaurant} />
                    )}

                    <Typography className={clsx(classes.sectionTitle)}>
                        {i18n.t('restaurant.assignGroupManagers')}
                    </Typography>
                    <FormikMultiSelect
                        name="selectedUsers"
                        options={users}
                        getOptionLabel={(option) => option.name}
                        error={touched.selectedUsers && errors.selectedUsers}
                        inputLabel={i18n.t('common.selectFromTheList')}
                        popupIcon={images.icons.triangleDown}
                        getOptionSelected={(option, value) => option.id === value.id}
                    />

                    {values.selectedUsers.length > 0 && (
                        <Chips
                            items={values.selectedUsers}
                            onDelete={(option) =>
                                handleUserRemove(option, values.selectedUsers, setFieldValue)
                            }
                        />
                    )}
                </Grid>

                <Grid item xs={12} md={6} lg={5}>
                    <Typography className={classes.sectionTitle}>
                        {i18n.t('restaurant.restaurantMenu')}
                    </Typography>
                    {!isEditMode && (
                        <FormControl component="fieldset" className={classes.mBot15}>
                            <RadioGroup
                                name="menuOption"
                                value={values.menuOption}
                                onChange={(e) => setFieldValue('menuOption', e.target.value)}
                            >
                                <FormControlLabel
                                    value={MenuOption.Empty}
                                    control={<Radio color="primary" />}
                                    label={i18n.t('restaurant.createEmptyCatalog')}
                                />
                                <FormControlLabel
                                    value={MenuOption.Copy}
                                    control={<Radio color="primary" />}
                                    label={i18n.t('restaurant.copyExistingMenu')}
                                />
                            </RadioGroup>
                        </FormControl>
                    )}

                    {values.menuOption === MenuOption.Empty && (
                        <Input
                            fullWidth
                            id="catalog-name"
                            name="catalogName"
                            type="text"
                            disabled={isEditMode}
                            error={touched.catalogName && errors.catalogName}
                            variant="filled"
                            placeholder={i18n.t('restaurant.enterCatalogName')}
                            label={i18n.t('restaurant.catalogName')}
                        />
                    )}

                    {!isEditMode && values.menuOption === MenuOption.Copy && (
                        <Autocomplete
                            options={menus}
                            getOptionLabel={(option) => option.name || ''}
                            value={values.selectedMenus?.[0] || null}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(event, newValue) => {
                                setFieldValue('selectedMenus', [newValue]);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    name="selectedMenus"
                                    variant={'filled'}
                                    label={i18n.t('common.selectFromTheList')}
                                    error={touched.selectedMenus && !!errors.selectedMenus}
                                    {...params}
                                />
                            )}
                        />
                    )}

                    {/* Select multiple menus for catalog creation */}
                    {/* {values.menuOption === MenuOption.Copy && (
                        <FormikMultiSelect
                            name="selectedMenu"
                            options={availableMenus}
                            getOptionLabel={(option) => option.name}
                            error={touched.selectedMenus && errors.selectedMenus}
                            inputLabel={i18n.t('common.selectFromTheList')}
                            popupIcon={images.icons.triangleDown}
                            getOptionSelected={(option, value) => option.id === value.id}
                        />
                    )} */}
                </Grid>
            </Grid>
        </Form>
    );
}
